import { CampaignType } from '@mm/state/model/CampaignsModel';
import { ReactComponent as FBIcon } from 'assets/icons/live-chat-fb.svg';
import { ReactComponent as SMSIcon } from 'assets/icons/sms.svg';
import { isNumber } from 'lodash';
import React from 'react';
import {
  Grid,
  GridCell,
  GridLabels,
  GridRow,
} from '../../../../../common/components/Grid/Grid';
import { PercentageBar } from '../../../../../common/components/PercentageBar/PercentageBar';
import './ChatBlastReportStatistics.scss';
import ChatBlastReportStatisticsGraph from './ChatBlastReportStatisticsGraph/ChatBlastReportStatisticsGraph';

type Props = {
  blast: CampaignType;
  isSMS?: boolean;
  isFacebook?: boolean;
};

export const ChatBlastReportStatisticsTable = ({
  blast,
  isSMS,
  isFacebook,
}: Props) => {
  const {
    readCount,
    respondedCount,
    undeliverableCount,
    smsReadCount,
    smsRespondedCount,
  } = blast;

  return (
    <div className="blast-report-statistics__numbers-table">
      <Grid>
        <GridLabels className="blast-report-statistics__numbers-table-header-row">
          <GridCell fr={1.5}>
            <div />
          </GridCell>
          <GridCell>Read</GridCell>
          <GridCell>Responded</GridCell>
          <GridCell>Undeliverables</GridCell>
        </GridLabels>

        {isFacebook && (
          <GridRow className="blast-report-statistics__numbers-table-row">
            <GridCell
              className="blast-report-statistics__numbers-table-first-cell"
              fr={1.5}
            >
              <FBIcon className="blast-report-statistics__numbers-table-cell-icon" />
              <span>Facebook Messenger</span>
            </GridCell>
            <GridCell className="blast-report-statistics__numbers-table-cell">
              <strong>{readCount}</strong>
            </GridCell>
            <GridCell className="blast-report-statistics__numbers-table-cell">
              <strong>{respondedCount}</strong>
            </GridCell>
            <GridCell className="blast-report-statistics__numbers-table-cell">
              <strong>{undeliverableCount}</strong>
            </GridCell>
          </GridRow>
        )}
        {isSMS && (
          <GridRow className="blast-report-statistics__numbers-table-row">
            <GridCell
              className="blast-report-statistics__numbers-table-first-cell"
              fr={1.5}
            >
              <div className="blast-report-statistics__numbers-table-cell-icon">
                <SMSIcon />
              </div>
              <span>SMS</span>
            </GridCell>
            <GridCell className="blast-report-statistics__numbers-table-cell">
              <strong> N/A</strong>
            </GridCell>
            <GridCell className="blast-report-statistics__numbers-table-cell">
              <strong>N/A</strong>
            </GridCell>
            <GridCell className="blast-report-statistics__numbers-table-cell">
              <strong> N/A</strong>
            </GridCell>
          </GridRow>
        )}
      </Grid>
    </div>
  );
};

const ChatBlastReportStatistics = ({ blast }: Props) => {
  const { schedulersAttributes } = blast;
  const isSMS = blast.platforms?.includes('sms');
  const isFacebook = blast.platforms?.includes('facebook');
  const scheduler = schedulersAttributes && schedulersAttributes[0];

  if (!scheduler) {
    return (
      <div className="blast-report-statistics">
        There are no statistics available at this time, please check again
        later.
      </div>
    );
  }

  const { openRate, responseRate, smsResponseRate = 0 } = blast;

  const {
    readOverTime,
    respondedOverTime,
    smsRespondedOverTime = {},
  } = scheduler;

  return (
    <div className="blast-report-statistics">
      <div className="blast-report-statistics__numbers-block">
        <ChatBlastReportStatisticsTable
          blast={blast}
          isSMS={isSMS}
          isFacebook={isFacebook}
        />
      </div>
      <div className="blast-report-statistics__progress-container">
        {isFacebook && (
          <div className="blast-report-statistics__progress-wrapper">
            <div className="blast-report-statistics__progress-block">
              <div className="blast-report-statistics__progress-header">
                <div className="blast-report-statistics__progress-label">
                  Read Rate (Facebook Messenger)
                </div>
                <div className="blast-report-statistics__progress-value">
                  {openRate}% of 100%
                </div>
              </div>
              <div className="blast-report-statistics__progress-bar">
                <PercentageBar
                  customPercentClass="percentage-bar__blue"
                  bodyWrapperStyle="blast-report-statistics__progress-bar-wrapper"
                  used={openRate}
                  total={100}
                  useOverTotal={false}
                  showTotalNumber={false}
                />
              </div>
            </div>

            <div className="blast-report-statistics__progress-block">
              <div className="blast-report-statistics__progress-header">
                <div className="blast-report-statistics__progress-label">
                  Response Rate (Facebook Messenger)
                </div>
                <div className="blast-report-statistics__progress-value">
                  {responseRate}% of 100%
                </div>
              </div>
              <div className="blast-report-statistics__progress-bar">
                <PercentageBar
                  customPercentClass="percentage-bar__denim"
                  bodyWrapperStyle="blast-report-statistics__progress-bar-wrapper"
                  used={responseRate}
                  total={100}
                  useOverTotal={false}
                  showTotalNumber={false}
                />
              </div>
            </div>
          </div>
        )}

        {isSMS && (
          <div className="blast-report-statistics__progress-wrapper blast-report-statistics__progress-wrapper--reverse">
            <div className="blast-report-statistics__progress-block">
              <div className="blast-report-statistics__progress-header">
                <div className="blast-report-statistics__progress-label">
                  Response Rate (SMS)
                </div>
                <div className="blast-report-statistics__progress-value">
                  N/A
                </div>
              </div>
              <div className="blast-report-statistics__progress-bar">
                <PercentageBar
                  used={smsResponseRate}
                  total={100}
                  customPercentClass="percentage-bar__purple"
                  bodyWrapperStyle="blast-report-statistics__progress-bar-wrapper"
                  useOverTotal={false}
                  showTotalNumber={false}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="blast-report-statistics__chart-block">
        {respondedOverTime &&
          readOverTime &&
          (Object.prototype.hasOwnProperty.call(respondedOverTime, 1) ||
            Object.prototype.hasOwnProperty.call(readOverTime, 1) ||
            Object.prototype.hasOwnProperty.call(smsRespondedOverTime, 1)) && (
            <ChatBlastReportStatisticsGraph
              responded={respondedOverTime}
              read={readOverTime}
              smsResponded={smsRespondedOverTime}
              isSMS={isSMS}
              isFacebook={isFacebook}
            />
          )}
      </div>
    </div>
  );
};

export default ChatBlastReportStatistics;
